<template>
  <div class="report-details-page">
    <!-- Page Header -->
    <PageHeader
      title="Report Details"
      :breadcrumbs="[
        { label: 'Financial Assistant', to: { name: 'chatbot' }, icon: 'las la-home' },
        { label: `${candidateName}`, to: { name: 'chatbot-candidate', params: { candidateId } } },
        {
          label: 'Automated Assessment',
          to: { name: 'chatbot-automated-assessment-input', params: { candidateId } }
        },
        { label: 'Report Details' }
      ]"
    />

    <!-- Main Content -->
    <div class="main-content">
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>Loading report details...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <div class="error-icon">
          <i class="las la-exclamation-triangle"></i>
        </div>
        <h3>Error Loading Report</h3>
        <p>{{ error }}</p>
        <div class="error-actions">
          <button @click="loadReportAnalysis" class="retry-btn">
            <i class="las la-redo"></i>
            Try Again
          </button>
          <BaseButton
            stylevariant="primary"
            :cus_style="{ width: 'auto', padding: '0.4rem 0.875rem', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }"
            @click="goToAutomatedAssessment"
          >
            <i class="las la-arrow-left"></i>
            Automated Assessment
          </BaseButton>
        </div>
      </div>

      <div v-else-if="reportAnalysis" class="report-details-content">
        <div class="report-back-action">
          <BaseButton
            stylevariant="primary"
            :cus_style="{ width: 'auto', padding: '0.4rem 0.875rem', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }"
            @click="goToAutomatedAssessment"
          >
            <i class="las la-arrow-left"></i>
            Automated Assessment
          </BaseButton>
        </div>

        <!-- Report Overview Card -->
        <div class="report-overview-card">
          <div class="report-header">
            <div class="report-info">
              <h2 class="report-title">Credit Model Report</h2>
              <div class="report-meta">
                <span class="file-name" v-if="fileName">{{ fileName }}</span>
                <span class="report-date">{{ formatDate(new Date()) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Credit Model Report Content -->
        <div v-if="creditModelData" class="credit-model-content">
          <!-- Report editor: inputs left, paginated preview right -->
          <div
            class="report-editor-layout"
            tabindex="0"
            @keydown.left="goToPrevPreviewPage"
            @keydown.right="goToNextPreviewPage"
          >
            <div ref="reportInputsPanel" class="report-inputs-panel">
              <h3 class="panel-title">Report Content</h3>
              <p class="panel-page-label">
                Page {{ currentPreviewPage }} — {{ currentPreviewPageTitle }}
              </p>

              <div v-if="currentPreviewPage === 2" class="report-page-inputs">
                <div class="form-section">
                  <label class="section-title">Company Requirement</label>
                  <textarea
                    v-model="companyRequirement"
                    class="textarea-input"
                    placeholder="Enter company requirement..."
                    rows="4"
                    @input="onRequirementChange"
                  ></textarea>
                </div>
                <div class="form-section">
                  <label class="section-title">Key Authorised Signatory</label>
                  <input
                    v-model="keyAuthorisedSignatory"
                    type="text"
                    class="text-input"
                    placeholder="Enter key authorised signatory..."
                    @input="onKeyAuthorisedSignatoryChange"
                  />
                </div>
              </div>

              <div v-else-if="currentPreviewPage === 3 && analysisData" class="report-page-inputs">
                <div
                  v-if="analysisData.credit_recommendation !== undefined"
                  class="form-section"
                >
                  <label class="section-title">Credit Recommendation</label>
                  <div class="recommendation-content">
                    <div v-if="!editingRecommendation" class="recommendation-display">
                      <span
                        class="recommendation-badge-compact"
                        :class="getRecommendationClass(analysisData.credit_recommendation)"
                      >
                        {{ formatCreditRecommendation(analysisData.credit_recommendation) }}
                      </span>
                      <i
                        @click="editingRecommendation = true"
                        class="las la-edit edit-icon-small"
                        title="Edit"
                      ></i>
                    </div>
                    <div v-else class="recommendation-edit">
                      <select v-model="editingRecommendationValue" class="recommendation-select">
                        <option value="Approved">Approved</option>
                        <option value="Rejected">Rejected</option>
                      </select>
                      <i
                        @click="saveRecommendation"
                        class="las la-check save-icon-small"
                        title="Save"
                      ></i>
                      <i
                        @click="cancelEditRecommendation"
                        class="las la-times cancel-icon-small"
                        title="Cancel"
                      ></i>
                    </div>
                  </div>
                </div>
                <div class="report-preview-inputs-grid">
                  <AnalysisCard
                    title="Key Strengths"
                    :items="analysisData.key_strengths"
                    card-class="strengths-card"
                    @update="updateAnalysisData('key_strengths', $event)"
                  />
                  <AnalysisCard
                    title="Key Weaknesses"
                    :items="analysisData.key_weaknesses"
                    card-class="weaknesses-card"
                    @update="updateAnalysisData('key_weaknesses', $event)"
                  />
                </div>
              </div>

              <div v-else-if="currentPreviewPage === 4" class="report-page-inputs">
                <div class="report-preview-inputs-grid">
                  <div class="form-section">
                    <label class="section-title">Company Description</label>
                    <textarea
                      v-model="companyDescription"
                      class="textarea-input"
                      placeholder="Enter company description..."
                      rows="4"
                      @input="onDescriptionChange"
                    ></textarea>
                  </div>
                  <div class="form-section">
                    <label class="section-title">Sales (AED) (as per VAT)</label>
                    <input
                      v-model="salesAsPerVat"
                      type="text"
                      class="text-input"
                      placeholder="Enter sales as per VAT..."
                      @input="onSalesAsPerVatChange"
                    />
                  </div>
                </div>
                <AnalysisCard
                  v-if="analysisData"
                  title="Revenue Notes"
                  :items="revenueNotesItems"
                  card-class="revenue-notes-card"
                  :always-show="true"
                  @update="updateAnalysisData('revenue_notes', $event)"
                />
              </div>

              <div v-else-if="currentPreviewPage === 5 && analysisData" class="report-page-inputs">
                <AnalysisCard
                  title="Yearly Sales Notes"
                  :items="yearlySalesNotesItems"
                  card-class="yearly-sales-notes-card"
                  :always-show="true"
                  @update="updateAnalysisData('yearly_sales_notes', $event)"
                />
              </div>

              <div v-else-if="currentPreviewPage === 6 && analysisData" class="report-page-inputs">
                <AnalysisCard
                  title="Cash & Quick Ratio Analysis"
                  :items="analysisData.cash_quick_ratio"
                  card-class="ratio-card"
                  @update="updateAnalysisData('cash_quick_ratio', $event)"
                />
              </div>

              <div v-else-if="currentPreviewPage === 8 && analysisData" class="report-page-inputs">
                <div class="report-preview-inputs-grid">
                  <AnalysisCard
                    title="Top 5 Payer Concentration Analysis"
                    :items="analysisData.payer_concentration_analysis"
                    card-class="payer-concentration-card"
                    @update="updateAnalysisData('payer_concentration_analysis', $event)"
                  />
                  <AnalysisCard
                    title="Prime Payers Overview"
                    :items="primePayersOverviewItems"
                    card-class="prime-payers-overview-card"
                    :always-show="true"
                    @update="updateAnalysisData('prime_payer_analysis', $event)"
                  />
                </div>
              </div>

              <div v-else-if="currentPreviewPage === 9 && analysisData" class="report-page-inputs">
                <div class="report-preview-inputs-grid">
                  <div class="form-section payer-stats-form-section">
                    <table class="payer-stats-table">
                      <thead>
                        <tr>
                          <th>Key Assessment Metrics - Prime Payers</th>
                          <th>Recent Value (last 12 months)</th>
                          <th>Lifetime Value (last 8 years)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(row, index) in payerStatRows"
                          :key="`prime-stat-${index}`"
                        >
                          <td class="label-cell">{{ row.label }}</td>
                          <td>
                            <input
                              :value="getPayerStatValue('prime_payer_stats', row.recentKey)"
                              type="text"
                              class="payer-stat-cell-input"
                              :placeholder="row.recentPlaceholder"
                              @input="updatePayerStat('prime_payer_stats', row.recentKey, $event.target.value)"
                            />
                          </td>
                          <td>
                            <input
                              :value="getPayerStatValue('prime_payer_stats', row.lifetimeKey)"
                              type="text"
                              class="payer-stat-cell-input"
                              :placeholder="row.lifetimePlaceholder"
                              @input="updatePayerStat('prime_payer_stats', row.lifetimeKey, $event.target.value)"
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <AnalysisCard
                    title="Kema Approved Payers Overview"
                    :items="approvedPayersOverviewItems"
                    card-class="approved-payers-overview-card"
                    :always-show="true"
                    @update="updateAnalysisData('approved_payer_analysis', $event)"
                  />
                  <div class="form-section payer-stats-form-section">
                    <table class="payer-stats-table">
                      <thead>
                        <tr>
                          <th>Key Assessment Metrics - Kema Approved Payers</th>
                          <th>Recent Value (last 12 months)</th>
                          <th>Lifetime Value (last 8 years)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(row, index) in payerStatRows"
                          :key="`kema-stat-${index}`"
                        >
                          <td class="label-cell">{{ row.kemaLabel || row.label }}</td>
                          <td>
                            <input
                              :value="getPayerStatValue('kema_approved_payer_stats', row.recentKey)"
                              type="text"
                              class="payer-stat-cell-input"
                              :placeholder="row.recentPlaceholder"
                              @input="updatePayerStat('kema_approved_payer_stats', row.recentKey, $event.target.value)"
                            />
                          </td>
                          <td>
                            <input
                              :value="getPayerStatValue('kema_approved_payer_stats', row.lifetimeKey)"
                              type="text"
                              class="payer-stat-cell-input"
                              :placeholder="row.lifetimePlaceholder"
                              @input="updatePayerStat('kema_approved_payer_stats', row.lifetimeKey, $event.target.value)"
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div v-else-if="currentPreviewPage === 10 && analysisData" class="report-page-inputs">
                <div class="report-preview-inputs-grid">
                  <AnalysisCard
                    title="Receivables Ageing Analysis"
                    :items="analysisData.aging_bucket_analysis"
                    card-class="aging-bucket-card"
                    @update="updateAnalysisData('aging_bucket_analysis', $event)"
                  />
                  <AnalysisCard
                    title="Receivables Payer Concentration Analysis"
                    :items="analysisData.rec_oustanding_cust_analysis"
                    card-class="rec-outstanding-card"
                    @update="updateAnalysisData('rec_oustanding_cust_analysis', $event)"
                  />
                </div>
              </div>

              <div v-else-if="currentPreviewPage === 11 && analysisData" class="report-page-inputs">
                <div class="report-preview-inputs-grid">
                  <AnalysisCard
                    title="Risk Strengths"
                    :items="relativeRiskStrengthsItems"
                    card-class="risk-strengths-card"
                    :always-show="true"
                    @update="updateAnalysisData('relative_risk_strengths', $event)"
                  />
                  <AnalysisCard
                    title="Risk Weaknesses"
                    :items="relativeRiskWeaknessesItems"
                    card-class="risk-weaknesses-card"
                    :always-show="true"
                    @update="updateAnalysisData('relative_risk_weaknesses', $event)"
                  />
                </div>
              </div>

              <div v-else-if="currentPreviewPage === 12 && analysisData" class="report-page-inputs">
                <div class="report-preview-inputs-grid">
                  <AnalysisCard
                    title="Conclusion"
                    :items="analysisData.conclusion_points"
                    card-class="conclusion-card"
                    @update="updateAnalysisData('conclusion_points', $event)"
                  />
                  <AnalysisCard
                    title="Recommendation Points"
                    :items="analysisData.recommendation_points"
                    card-class="recommendation-points-card"
                    @update="updateAnalysisData('recommendation_points', $event)"
                  />
                </div>
              </div>

              <div v-else-if="currentPreviewPage === 16 && analysisData" class="report-page-inputs">
                <AnalysisCard
                  title="Kema Approved Payers List"
                  :items="approvedPayersItems"
                  card-class="approved-payers-list-card"
                  :always-show="true"
                  @update="updateAnalysisData('approved_payers', $event)"
                />
              </div>

              <p v-else class="report-inputs-empty">
                No editable fields on this page. Use the arrows to continue.
              </p>
            </div>

            <div class="report-preview-column">
              <div class="report-page-navigator">
                <button
                  type="button"
                  class="report-page-nav-btn"
                  :disabled="currentPreviewPage <= 1"
                  aria-label="Previous page"
                  @click="goToPrevPreviewPage"
                >
                  <i class="las la-arrow-left"></i>
                </button>
                <div class="report-page-nav-meta">
                  <span class="report-page-nav-count">
                    Page {{ currentPreviewPage }} / {{ totalPreviewPages }}
                  </span>
                  <span class="report-page-nav-title">{{ currentPreviewPageTitle }}</span>
                </div>
                <button
                  type="button"
                  class="report-page-nav-btn"
                  :disabled="currentPreviewPage >= totalPreviewPages"
                  aria-label="Next page"
                  @click="goToNextPreviewPage"
                >
                  <i class="las la-arrow-right"></i>
                </button>
              </div>

              <div class="report-preview-panel">
                <CreditAssessmentReportPreview
                  ref="reportPreview"
                  embedded
                  single-page-view
                  :visible-page="currentPreviewPage"
                  :data="reportPreviewData"
                />
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatbotService from '@/services/chatbot.service'
import BaseButton from '@/components/UI/BaseButton.vue'
import AnalysisCard from './components/creditReport/AnalysisCard.vue'
import PageHeader from './components/PageHeader.vue'
import CreditAssessmentReportPreview from './components/CreditAssessmentReportPreview.vue'
import {
  REPORT_PREVIEW_PAGES,
  REPORT_PREVIEW_PAGE_COUNT
} from './constants/reportEditorPages.js'

const PAYER_STAT_ROWS = [
  {
    label: 'Average Length of Relationship',
    recentKey: 'lor_recent',
    lifetimeKey: 'lor_lifetime',
    recentPlaceholder: 'NA',
    lifetimePlaceholder: 'Months'
  },
  {
    label: 'Average Payment Completion Ratio',
    recentKey: 'pcr_recent',
    lifetimeKey: 'pcr_lifetime',
    recentPlaceholder: '%',
    lifetimePlaceholder: '%'
  },
  {
    label: 'Average <=60 Days Payment Completion Ratio',
    recentKey: 'six_dpr_recent',
    lifetimeKey: 'six_dpr_lifetime',
    recentPlaceholder: '%',
    lifetimePlaceholder: '%'
  },
  {
    label: 'Weighted Average Days to Pay (ADTP)',
    kemaLabel: 'Weighted ADTP',
    recentKey: 'adtp_recent',
    lifetimeKey: 'adtp_lifetime',
    recentPlaceholder: 'Days',
    lifetimePlaceholder: 'Days'
  }
]

const EMPTY_PAYER_STATS = {
  lor_recent: '',
  lor_lifetime: '',
  pcr_recent: '',
  pcr_lifetime: '',
  six_dpr_recent: '',
  six_dpr_lifetime: '',
  adtp_recent: '',
  adtp_lifetime: ''
}

export default {
  name: 'ReportDetails',
  components: {
    BaseButton,
    PageHeader,
    AnalysisCard,
    CreditAssessmentReportPreview
  },
  props: {
    candidateId: {
      type: String,
      required: true
    },
    fileId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      candidateName: '',
      reportAnalysis: null,
      fileName: null,
      loading: false,
      error: null,
      editingRecommendation: false,
      editingRecommendationValue: '',
      candidate: null,
      companyRequirement: '',
      companyDescription: '',
      keyAuthorisedSignatory: '',
      salesAsPerVat: '',
      reportContext: {},
      payerStatRows: PAYER_STAT_ROWS,
      currentPreviewPage: 1
    }
  },
  watch: {
    'analysisData.credit_recommendation': {
      immediate: true,
      handler(newVal) {
        if (newVal !== undefined && newVal !== null && newVal !== '') {
          this.editingRecommendationValue = this.normalizeCreditRecommendation(newVal)
        }
      }
    },
    
    'reportAnalysis.company_requirement': {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.companyRequirement = newVal
        }
      }
    },
    
    'reportAnalysis.company_overview': {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.companyDescription = newVal
        }
      }
    },

    'reportAnalysis.key_authorised_signatory': {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.keyAuthorisedSignatory = String(newVal)
        }
      }
    },

    'reportAnalysis.sales_as_per_vat': {
      immediate: true,
      handler(newVal) {
        if (newVal !== undefined && newVal !== null && newVal !== '') {
          this.salesAsPerVat = String(newVal)
        }
      }
    }
  },
  async mounted() {
    await this.loadCandidate()
    await Promise.all([
      this.loadReportContext(),
      this.loadReportAnalysis(),
      this.loadFileName()
    ])
    this.initializeReportInputDefaults()
    this.initializePayerConcentrationAnalysis()
    this.initializeRevenueNotes()
    this.initializeYearlySalesNotes()
    this.initializeReceivablesAnalysis()
    this.initializePayerStatsFields()
    this.initializeRelativeRiskAnalysis()
  },
  computed: {
    creditModelData() {
      if (!this.reportAnalysis) return null
      return this.reportAnalysis
    },
    
    analysisData() {
      if (!this.reportAnalysis) return null
      return this.reportAnalysis.analysis_data || null
    },

    approvedPayersOverviewItems() {
      if (!this.analysisData) return []
      return Array.isArray(this.analysisData.approved_payer_analysis)
        ? this.analysisData.approved_payer_analysis
        : []
    },

    primePayersOverviewItems() {
      if (!this.analysisData) return []
      return Array.isArray(this.analysisData.prime_payer_analysis)
        ? this.analysisData.prime_payer_analysis
        : []
    },

    salesTrendAnalysisItems() {
      if (!this.analysisData) return []
      return Array.isArray(this.analysisData.sales_trend_analysis)
        ? this.analysisData.sales_trend_analysis
        : []
    },

    revenueNotesItems() {
      if (!this.analysisData) return []
      const notes = this.analysisData.revenue_notes
      if (Array.isArray(notes)) return notes
      if (typeof notes === 'string' && notes.trim()) return [notes.trim()]
      return []
    },

    yearlySalesNotesItems() {
      if (!this.analysisData) return []
      const notes = this.analysisData.yearly_sales_notes
      if (Array.isArray(notes)) return notes
      if (typeof notes === 'string' && notes.trim()) return [notes.trim()]
      return []
    },

    approvedPayersItems() {
      if (!this.analysisData) return []
      return Array.isArray(this.analysisData.approved_payers)
        ? this.analysisData.approved_payers
        : []
    },

    relativeRiskStrengthsItems() {
      if (!this.analysisData) return []
      return Array.isArray(this.analysisData.relative_risk_strengths)
        ? this.analysisData.relative_risk_strengths
        : []
    },

    relativeRiskWeaknessesItems() {
      if (!this.analysisData) return []
      return Array.isArray(this.analysisData.relative_risk_weaknesses)
        ? this.analysisData.relative_risk_weaknesses
        : []
    },

    totalPreviewPages() {
      return REPORT_PREVIEW_PAGE_COUNT
    },

    currentPreviewPageTitle() {
      const match = REPORT_PREVIEW_PAGES.find(
        (entry) => entry.page === this.currentPreviewPage
      )

      return match?.title || ''
    },
    reportPreviewData() {
      const context = this.reportContext || {};
      const analysis = this.analysisData || {};
      const merchantName = this.getReportContextValue(
        'merchant_name',
        'merchantName'
      ) || this.candidate?.name || this.candidate?.company || this.candidateName || 'Merchant Name';

      const riskScore = this.resolveGrandTotalRiskScore(context)

      const assessmentHighlights = this.resolveAssessmentHighlights(context);

      const keyStrengths = Array.isArray(analysis.key_strengths) && analysis.key_strengths.length
        ? analysis.key_strengths
        : (Array.isArray(context.key_strengths) ? context.key_strengths : []);

      const keyWeaknesses = Array.isArray(analysis.key_weaknesses) && analysis.key_weaknesses.length
        ? analysis.key_weaknesses
        : (Array.isArray(context.key_weaknesses) ? context.key_weaknesses : []);

      const creditRecommendation = analysis.credit_recommendation
        ?? this.getReportContextValue('credit_recommendation', 'creditRecommendation');

      const recommendationRows = context.recommendation_rows || [];

      const monthlySalesTrend = this.resolveMonthlySalesTrend(context);
      const ratioTrends = this.resolveRatioTrends(context);

      return {
        merchantName,
        preparedBy: this.getReportContextValue('prepared_by') || '—',
        reportDate: this.formatContextDate(
          this.getReportContextValue('assessment_date')
        ) || this.formatDisplayDate(new Date()),
        establishmentDate: this.formatContextDate(
          this.getReportContextValue(
            'establishment_date',
            'establishmentDate',
            'business_incorporation_date'
          )
        ),
        industry: this.getReportContextValue(
          'industry',
          'main_sector_type',
          'mainSectorType',
          'subsector',
          'main_sector'
        ) || '—',
        keyShareholder: this.keyAuthorisedSignatory?.trim()
          || this.resolveShareholdersDisplay(context)
          || '',
        companyOverview: this.companyRequirement?.trim() || '',
        facilityRequested: this.getReportContextValue(
          'facility_requested',
          'facilityRequested'
        ) || 'Invoice Financing',
        facilityLimit: this.formatContextCurrency(
          this.getReportContextValue('facility_limit')
        ),
        tenorRequested: this.getReportContextValue('facility_tenor') || '—',
        assessmentHighlights,
        riskScore,
        riskGrade: this.getReportContextValue('risk_grade', 'riskGrade')
          || this.getRiskGrade(riskScore),
        keyStrengths,
        keyWeaknesses,
        creditRecommendation: this.formatCreditRecommendation(creditRecommendation),
        recommendationRows: Array.isArray(recommendationRows) ? recommendationRows : [],
        businessProfilePeriod: this.getReportContextValue(
          'period_range',
          'business_profile_period',
          'profile_period',
          'sales_period'
        ) || '—',
        businessOverview: this.companyDescription?.trim() || '',
        salesAsPerVat: this.formatSalesAsPerVatInput(this.salesAsPerVat),
        netSalesAsPerErp: this.formatNetSales(
          this.getReportContextValue(
            'revenue_erp'
          )
        ),
        monthlySalesTrend,
        yearlySalesTrend: this.resolveTrendArray(context, 'yearly_sales_trend'),
        yearlySalesNotes: this.resolveAnalysisField(
          analysis,
          context,
          'yearly_sales_notes'
        ),
        cashRatioTrend: ratioTrends.cashRatioTrend,
        workingCapitalRatioTrend: ratioTrends.workingCapitalRatioTrend,
        cashQuickRatioPoints: Array.isArray(analysis.cash_quick_ratio)
          ? analysis.cash_quick_ratio
          : [],
        averageMonthlySales: this.formatSalesMetric(
          this.getReportContextValue(
            'average_monthly_sales',
            'avg_monthly_sales'
          )
        ),
        minSales: this.formatSalesMetric(
          this.getReportContextValue('min_sales', 'minimum_sales')
        ),
        maxSales: this.formatSalesMetric(
          this.getReportContextValue('max_sales', 'maximum_sales')
        ),
        revenueNotes: this.resolveAnalysisField(
          analysis,
          context,
          'revenue_notes',
          'sales_trend_analysis',
          'monthly_sales_analysis',
          'business_profile_analysis'
        ),
        salesTrendAnalysis: this.resolveAnalysisField(
          analysis,
          context,
          'sales_trend_analysis',
          'monthly_sales_analysis',
          'business_profile_analysis'
        ),
        primePayers: this.resolvePrimePayers(context),
        debts: this.resolveDebts(context),
        debtsTotal: this.resolveDebtsTotal(context),
        payerConcentrationTrend: this.resolvePayerConcentrationTrend(context),
        payerConcentrationAnalysis: this.resolveAnalysisField(
          analysis,
          context,
          'payer_concentration_analysis'
        ),
        primePayersOverview: this.resolveAnalysisField(
          analysis,
          context,
          'prime_payer_analysis'
        ),
        agingBucketTrend: this.resolveTrendArray(context, 'aging_bucket_trend'),
        agingBucketAnalysis: this.resolveAnalysisField(
          analysis,
          context,
          'aging_bucket_analysis'
        ),
        recOutstandingTrend: this.resolveTrendArray(
          context,
          'rec_oustanding_cust_trend',
          'rec_outstanding_cust_trend'
        ),
        recOutstandingAnalysis: this.resolveAnalysisField(
          analysis,
          context,
          'rec_oustanding_cust_analysis',
          'rec_outstanding_cust_analysis'
        ),
        primePayerStats: this.resolvePayerStatsObject(
          analysis.prime_payer_stats,
          context.prime_payer_stats
        ),
        primePayerPcrFootnote: analysis.prime_payer_pcr_footnote
          || context.prime_payer_pcr_footnote
          || '',
        kemaApprovedPayerStats: this.resolvePayerStatsObject(
          analysis.kema_approved_payer_stats,
          context.approved_payer_stats
        ),
        approvedPayersOverview: this.resolveAnalysisField(
          analysis,
          context,
          'approved_payer_analysis'
        ),
        approvedPayers: this.resolveAnalysisField(
          analysis,
          context,
          'approved_payers'
        ),
        riskSummary: this.resolveRiskSummary(context),
        relativeRiskStrengths: this.resolveAnalysisField(
          analysis,
          context,
          'relative_risk_strengths',
          'detailed_strengths'
        ),
        relativeRiskWeaknesses: this.resolveAnalysisField(
          analysis,
          context,
          'relative_risk_weaknesses',
          'detailed_weaknesses'
        ),
        conclusionPoints: Array.isArray(analysis.conclusion_points)
          ? analysis.conclusion_points
          : [],
        recommendationPoints: Array.isArray(analysis.recommendation_points)
          ? analysis.recommendation_points
          : []
      };
    }
    
  },
  methods: {
    goToAutomatedAssessment() {
      this.$router.push({
        name: 'chatbot-automated-assessment-input',
        params: { candidateId: this.candidateId }
      });
    },

    async loadReportAnalysis() {
      this.loading = true
      this.error = null
      
      try {
        const fileDetails = await ChatbotService.getFileDetails(this.fileId)
        this.reportAnalysis = fileDetails.transactions[0]
        console.log('reportAnalysis', this.reportAnalysis)
        
        this.initializeFieldsFromReportAnalysis()
        this.initializePayerConcentrationAnalysis()
        this.initializeRevenueNotes()
        this.initializeYearlySalesNotes()
        this.initializeReceivablesAnalysis()
        this.initializePayerStatsFields()
        this.initializeRelativeRiskAnalysis()
      } catch (err) {
        console.error('Error loading report analysis:', err)
        this.error = 'Failed to load report analysis. Please try again.'
      } finally {
        this.loading = false
      }
    },
    
    initializeFieldsFromReportAnalysis() {
      if (!this.reportAnalysis) return
      
      // Company requirement
      if (this.reportAnalysis.company_requirement) {
        this.companyRequirement = this.reportAnalysis.company_requirement
      }
      
      // Company description
      if (this.reportAnalysis.company_overview) {
        this.companyDescription = this.reportAnalysis.company_overview
      }

      if (this.reportAnalysis.key_authorised_signatory) {
        this.keyAuthorisedSignatory = String(this.reportAnalysis.key_authorised_signatory)
      }

      if (this.reportAnalysis.sales_as_per_vat !== undefined
        && this.reportAnalysis.sales_as_per_vat !== null
        && this.reportAnalysis.sales_as_per_vat !== '') {
        this.salesAsPerVat = String(this.reportAnalysis.sales_as_per_vat)
      }
    },

    initializePayerConcentrationAnalysis() {
      if (this.analysisData && !Array.isArray(this.analysisData.prime_payer_analysis)) {
        this.$set(this.analysisData, 'prime_payer_analysis', [])
      }

      this.initializeAnalysisFromContext(
        'payer_concentration_analysis',
        'payer_concentration_analysis'
      );
      this.initializeAnalysisFromContext(
        'prime_payer_analysis',
        'prime_payer_analysis'
      );
    },

    initializeRevenueNotes() {
      if (!this.analysisData) return;

      if (!Array.isArray(this.analysisData.revenue_notes)) {
        const existingNotes = this.analysisData.revenue_notes;
        if (typeof existingNotes === 'string' && existingNotes.trim()) {
          this.$set(this.analysisData, 'revenue_notes', [existingNotes.trim()]);
        } else {
          this.$set(this.analysisData, 'revenue_notes', []);
        }
      }

      this.initializeAnalysisFromContext(
        'revenue_notes',
        'revenue_notes',
        'sales_trend_analysis',
        'monthly_sales_analysis',
        'business_profile_analysis'
      );

      const existing = this.analysisData.revenue_notes;
      if (Array.isArray(existing) && existing.length) return;

      const contextValue = this.getReportContextValue(
        'revenue_notes',
        'sales_trend_analysis',
        'monthly_sales_analysis',
        'business_profile_analysis'
      );

      if (typeof contextValue === 'string' && contextValue.trim()) {
        this.$set(this.analysisData, 'revenue_notes', [contextValue.trim()]);
        return;
      }

      const fallback = this.analysisData.sales_trend_analysis;
      if (Array.isArray(fallback) && fallback.length) {
        this.$set(this.analysisData, 'revenue_notes', [...fallback]);
      }
    },

    initializeYearlySalesNotes() {
      if (!this.analysisData) return;

      if (!Array.isArray(this.analysisData.yearly_sales_notes)) {
        const existingNotes = this.analysisData.yearly_sales_notes;
        if (typeof existingNotes === 'string' && existingNotes.trim()) {
          this.$set(this.analysisData, 'yearly_sales_notes', [existingNotes.trim()]);
        } else {
          this.$set(this.analysisData, 'yearly_sales_notes', []);
        }
      }

      this.initializeAnalysisFromContext(
        'yearly_sales_notes',
        'yearly_sales_notes'
      );
    },

    initializeReceivablesAnalysis() {
      this.initializeAnalysisFromContext(
        'aging_bucket_analysis',
        'aging_bucket_analysis'
      );
      this.initializeAnalysisFromContext(
        'rec_oustanding_cust_analysis',
        'rec_oustanding_cust_analysis',
        'rec_outstanding_cust_analysis'
      );
    },

    initializeRelativeRiskAnalysis() {
      this.initializeAnalysisFromContext(
        'relative_risk_strengths',
        'detailed_strengths'
      );
      this.initializeAnalysisFromContext(
        'relative_risk_weaknesses',
        'detailed_weaknesses'
      );
    },

    initializePayerStatsFields() {
      if (this.analysisData && !Array.isArray(this.analysisData.approved_payer_analysis)) {
        this.$set(this.analysisData, 'approved_payer_analysis', [])
      }

      if (this.analysisData && !Array.isArray(this.analysisData.approved_payers)) {
        this.$set(this.analysisData, 'approved_payers', [])
      }

      this.initializeStatsObject(
        'prime_payer_stats',
        this.reportContext?.prime_payer_stats,
        (stats) => this.mapPrimePayerStatsFromContext(stats)
      );
      this.initializeStatsObject(
        'kema_approved_payer_stats',
        this.reportContext?.approved_payer_stats,
        (stats) => this.mapPrimePayerStatsFromContext(stats)
      );
      this.initializeAnalysisFromContext(
        'approved_payer_analysis',
        'approved_payer_analysis'
      );
      this.initializeAnalysisFromContext(
        'approved_payers',
        'approved_payers'
      );
    },

    initializeStatsObject(field, contextSource, mapper) {
      if (!this.analysisData || typeof mapper !== 'function') return;

      const existing = this.analysisData[field];

      if (existing && typeof existing === 'object') {
        const valueKeys = field === 'prime_payer_stats' || field === 'kema_approved_payer_stats'
          ? [
            'lor_lifetime',
            'pcr_recent',
            'pcr_lifetime',
            'six_dpr_recent',
            'six_dpr_lifetime',
            'adtp_recent',
            'adtp_lifetime'
          ]
          : Object.keys(EMPTY_PAYER_STATS);
        const hasValues = valueKeys.some((key) => {
          const value = existing[key];
          return value !== undefined && value !== null && String(value).trim() !== '';
        });

        if (hasValues) return;
      }

      this.$set(this.analysisData, field, mapper(contextSource));
    },

    mapPrimePayerStatsFromContext(stats) {
      if (!stats || typeof stats !== 'object') {
        return {
          ...EMPTY_PAYER_STATS,
          lor_recent: 'NA'
        };
      }

      return {
        lor_recent: 'NA',
        lor_lifetime: this.formatStatInput(stats.lor_sum),
        pcr_recent: this.formatStatInput(stats.pcr_sum),
        pcr_lifetime: this.formatStatInput(stats.pcr_lifetime_sum),
        six_dpr_recent: this.formatStatInput(stats.six_dpr_sum),
        six_dpr_lifetime: this.formatStatInput(stats.six_dpr_lifetime_sum),
        adtp_recent: this.formatStatInput(stats.adtp_sum),
        adtp_lifetime: this.formatStatInput(stats.adtp_lifetime_sum)
      };
    },

    resolvePayerStatsObject(analysisStats, contextStats) {
      const mappedContext = this.mapPrimePayerStatsFromContext(contextStats);
      const savedStats = analysisStats && typeof analysisStats === 'object'
        ? analysisStats
        : {};

      return {
        ...mappedContext,
        ...savedStats
      };
    },

    formatStatInput(value) {
      if (value === undefined || value === null || value === '') return '';
      return String(value);
    },

    getPayerStatValue(field, key) {
      return this.analysisData?.[field]?.[key] ?? '';
    },

    updatePayerStat(field, key, value) {
      const currentStats = {
        ...(this.analysisData?.[field] || EMPTY_PAYER_STATS)
      };

      this.$set(currentStats, key, value);
      this.updateAnalysisData(field, currentStats);
    },

    initializeAnalysisFromContext(field, ...contextKeys) {
      if (!this.analysisData) return;

      let fromContext = null;

      for (let index = 0; index < contextKeys.length; index += 1) {
        const key = contextKeys[index];
        const value = this.reportContext?.[key];

        if (Array.isArray(value) && value.length) {
          fromContext = value;
          break;
        }

        if (typeof value === 'string' && value.trim()) {
          fromContext = [value.trim()];
          break;
        }
      }

      if (!fromContext) return;

      const existing = this.analysisData[field];

      if (!Array.isArray(existing) || !existing.length) {
        this.$set(this.analysisData, field, [...fromContext]);
      }
    },

    resolveAnalysisField(analysis, context, field, ...contextKeys) {
      const toList = (value) => {
        if (Array.isArray(value) && value.length) return value;
        if (typeof value === 'string' && value.trim()) return [value.trim()];
        return null;
      };

      const fromAnalysis = toList(analysis[field]);
      if (fromAnalysis) return fromAnalysis;

      for (let index = 0; index < contextKeys.length; index += 1) {
        const fromContext = toList(context[contextKeys[index]]);
        if (fromContext) return fromContext;
      }

      const fromField = toList(context[field]);
      if (fromField) return fromField;

      return [];
    },

    resolveTrendArray(context, ...keys) {
      for (let index = 0; index < keys.length; index += 1) {
        const trend = context[keys[index]];

        if (Array.isArray(trend)) {
          return trend.map((item) => ({
            label: item.label ?? item.name ?? item.bucket ?? '—',
            value: Number(item.value ?? item.amount ?? 0)
          }));
        }
      }

      return [];
    },

    async loadFileName() {
      try {
        const fileDetails = await ChatbotService.getFileDetails(this.fileId)
        this.fileName = fileDetails.name || fileDetails.file_name || 'Unknown File'
      } catch (err) {
        console.error('Error loading file name:', err)
        // Don't set error here, just use default
      }
    },

    formatDate(date) {
      if (!date) return 'Unknown'
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    
    formatPercentage(value) {
      if (value === null || value === undefined || value === '') return '0%'
      const num = typeof value === 'number' ? value : parseFloat(value)
      if (isNaN(num)) return String(value)
      return (num * 100).toFixed(0) + '%'
    },
    
    formatCurrency(value) {
      if (value === null || value === undefined || value === '') return '-'
      const num = typeof value === 'number' ? value : parseFloat(value)
      if (isNaN(num)) return String(value)
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(num)
    },
    
    formatCellValue(value) {
      if (value === null || value === undefined || value === '') return '-'
      if (typeof value === 'number') {
        // Format numbers with appropriate decimals
        if (Number.isInteger(value)) return value.toLocaleString()
        return value.toFixed(2)
      }
      return String(value)
    },
    
    getTableHeaders(table) {
      if (!Array.isArray(table) || table.length === 0) return []
      const firstRow = table[0]
      return Object.keys(firstRow)
    },
    
    getCellClass(key, value) {
      const classes = []
      if (key.includes('Score') || key.includes('Percentage')) {
        classes.push('score-cell')
      }
      if (key.includes('Risk') && typeof value === 'string' && value.includes('Risk')) {
        classes.push('risk-header-cell')
      }
      return classes.join(' ')
    },
    
    getRecommendationClass(recommendation) {
      const rec = this.normalizeCreditRecommendation(recommendation).toUpperCase()
      if (rec === 'REJECTED') return 'recommendation-reject'
      return 'recommendation-accept'
    },

    normalizeCreditRecommendation(value) {
      if (!value) return 'Approved'
      const normalized = String(value).trim().toUpperCase()
      if (normalized === 'REJECTED' || normalized === 'REJECT' || normalized === 'DECLINE') {
        return 'Rejected'
      }
      return 'Approved'
    },
    
    updateAnalysisData(field, value) {
      if (this.analysisData) {
        this.$set(this.analysisData, field, value)
        // Also update the reportAnalysis to keep it in sync
        if (this.reportAnalysis) {
          this.$set(this.reportAnalysis, 'analysis_data', { ...this.analysisData })
        }
      }
    },
    
    saveRecommendation() {
      if (this.analysisData && this.editingRecommendationValue) {
        const normalized = this.normalizeCreditRecommendation(this.editingRecommendationValue)
        this.$set(this.analysisData, 'credit_recommendation', normalized)
        this.updateAnalysisData('credit_recommendation', normalized)
        this.editingRecommendation = false
      }
    },

    cancelEditRecommendation() {
      this.editingRecommendation = false
      this.editingRecommendationValue = this.normalizeCreditRecommendation(
        this.analysisData?.credit_recommendation
      )
    },

    async loadCandidate() {
      this.candidate = await ChatbotService.getFinancingCandidate(this.candidateId)
      this.candidateName = this.candidate?.name || this.candidate?.company || 'Unknown Candidate'
    },

    async loadReportContext() {
      try {
        this.reportContext = await ChatbotService.getReportContext(this.candidateId) || {}
        this.initializePayerStatsFields()
        this.initializeRelativeRiskAnalysis()
        this.initializeRevenueNotes()
        this.initializeYearlySalesNotes()
      } catch (err) {
        console.error('Error loading report context:', err)
        this.reportContext = {}
      }
    },

    getReportContextValue(...keys) {
      const context = this.reportContext || {}

      for (let index = 0; index < keys.length; index += 1) {
        const key = keys[index]
        const value = context[key]

        if (value !== undefined && value !== null && value !== '') {
          return value
        }
      }

      return null
    },

    resolveAssessmentHighlights(context) {
      const highlights = context.assessment_highlights
        || context.assessmentHighlights
        || [];

      if (!Array.isArray(highlights) || !highlights.length) {
        return [];
      }

      return highlights.map((row) => ({
        label: row.label ?? '—',
        subtext: row.subtext ?? row.subText ?? '',
        value: row.value ?? '—'
      }));
    },

    resolveMonthlySalesTrend(context) {
      const trend = context.monthly_sales_trend
        || context.monthlySalesTrend
        || context.sales_trend
        || [];

      if (!Array.isArray(trend)) return [];

      return trend.map((item) => ({
        label: item.label ?? item.month ?? item.period ?? '—',
        value: Number(item.value ?? item.sales ?? item.amount ?? 0)
      }));
    },

    resolveRatioTrends(context) {
      const combined = context.ratio_trends || context.ratioTrends || [];

      if (!Array.isArray(combined)) {
        return {
          cashRatioTrend: [],
          workingCapitalRatioTrend: []
        };
      }

      return {
        cashRatioTrend: combined.map((item) => ({
          label: item.label ?? item.month ?? item.period ?? '—',
          value: Number(item.cash_ratio ?? item.cashRatio ?? 0)
        })),
        workingCapitalRatioTrend: combined.map((item) => ({
          label: item.label ?? item.month ?? item.period ?? '—',
          value: Number(
            item.working_capital_ratio
            ?? item.workingCapitalRatio
            ?? item.wcr
            ?? 0
          )
        }))
      };
    },

    resolvePayerConcentrationTrend(context) {
      const trend = context.payer_concentration_trend
        || context.payerConcentrationTrend
        || [];

      if (!Array.isArray(trend)) return [];

      return trend.map((item) => ({
        label: item.label ?? item.name ?? item.payer ?? '—',
        value: Number(item.value ?? item.percentage ?? item.percent ?? 0),
        sector: item.sector ?? item.industry ?? ''
      }));
    },

    resolvePrimePayers(context) {
      const payers = context.prime_payers|| [];

      if (!Array.isArray(payers)) return [];

      return payers.map((payer) => ({
        name: payer.name ?? '—',
        moodyRating: payer.moody_rating ?? '—',
        parent: payer.parent ?? '—'
      }));
    },

    resolveRiskSummary(context) {
      const summary = context.risk_summary;

      if (!summary || typeof summary !== 'object') {
        return {};
      }

      return summary;
    },

    resolveGrandTotalRiskScore(context) {
      const grandTotal = context.risk_summary?.['Grand Total'];

      if (grandTotal?.score !== undefined && grandTotal?.score !== null && grandTotal?.score !== '') {
        return grandTotal.score;
      }

      return this.getReportContextValue(
        'Grand Total (Recalculated WCR)',
        'Grand Total'
      );
    },

    resolveDebts(context) {
      const debts = context.debts || [];

      if (!Array.isArray(debts)) return [];

      return debts.map((debt) => ({
        lender: debt.lender ?? '—',
        approvalAmount: this.formatDebtTableAmount(
          debt.approval_amount ?? debt.approvalAmount
        ),
        repaymentAmount: this.formatDebtTableAmount(
          debt.repayment_interest_service ?? debt.repaymentInterestService
        )
      }));
    },

    resolveDebtsTotal(context) {
      const explicitTotal = this.getReportContextValue(
        'debts_total',
        'loans_liabilities_total'
      );

      if (explicitTotal !== undefined && explicitTotal !== null && explicitTotal !== '') {
        return this.formatDebtsBannerTotal(explicitTotal);
      }

      const debts = context.debts || [];
      if (!Array.isArray(debts) || !debts.length) return '—';

      const total = debts.reduce((sum, debt) => {
        const amount = Number(debt.approval_amount ?? debt.approvalAmount ?? 0);
        return sum + (Number.isNaN(amount) ? 0 : amount);
      }, 0);

      return this.formatDebtsBannerTotal(total);
    },

    formatDebtTableAmount(value) {
      if (value === undefined || value === null || value === '') return '—';
      const number = Number(value);
      if (Number.isNaN(number)) return String(value);
      if (Number.isInteger(number)) {
        return number.toLocaleString('en-US');
      }
      return number.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
    },

    formatDebtsBannerTotal(value) {
      if (value === undefined || value === null || value === '') return '—';
      if (typeof value === 'string' && value.trim().toUpperCase().startsWith('AED')) {
        return value;
      }
      return this.formatSalesMetric(value);
    },

    formatNetSales(value) {
      if (value === undefined || value === null || value === '') return '—';
      if (typeof value === 'string' && value.trim().toUpperCase().startsWith('AED')) {
        return value;
      }
      return this.formatAmount(value);
    },

    formatSalesMetric(value) {
      if (value === undefined || value === null || value === '') return '—';
      if (typeof value === 'string') return value;
      const number = Number(value);
      if (Number.isNaN(number)) return String(value);
      if (number >= 1000000) {
        const millions = number / 1000000;
        const formatted = Number.isInteger(millions)
          ? millions
          : millions.toFixed(1).replace(/\.0$/, '');
        return `AED ${formatted}m`;
      }
      if (number >= 1000) {
        const thousands = number / 1000;
        const formatted = Number.isInteger(thousands)
          ? thousands
          : thousands.toFixed(1).replace(/\.0$/, '');
        return `AED ${formatted}k`;
      }
      return this.formatReportCurrency(number);
    },

    formatContextDate(value) {
      if (!value) return '—'
      if (typeof value === 'string' && /[A-Za-z]{3}\s+\d{1,2},\s+\d{4}/.test(value)) {
        return value
      }
      return this.formatDisplayDate(value)
    },

    formatContextCurrency(value) {
      if (value === undefined || value === null || value === '') return '—'
      if (typeof value === 'string' && value.trim().toUpperCase().startsWith('AED')) {
        return value
      }
      return this.formatReportCurrency(value)
    },

    formatDisplayDate(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    },

    formatAmount(value) {
      if (value === undefined || value === null || value === '') return '—'
      const number = Number(value)
      if (Number.isNaN(number)) return String(value)
      return number.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      })
    },

    formatReportCurrency(value) {
      if (value === undefined || value === null || value === '') return '—'
      const formatted = this.formatAmount(value)
      if (formatted === '—') return formatted
      return `AED ${formatted}`
    },

    getRiskGrade(score) {
      const numericScore = Number(score)
      if (Number.isNaN(numericScore)) return '—'
      if (numericScore >= 80) return 'Low Risk'
      if (numericScore >= 70) return 'Moderately Low Risk'
      if (numericScore >= 60) return 'Moderate Risk'
      if (numericScore >= 50) return 'Moderately High Risk'
      return 'High Risk'
    },

    formatSalesAsPerVatInput(value) {
      const trimmed = value?.trim()
      if (!trimmed) return '—'
      if (trimmed.toUpperCase().startsWith('AED')) return trimmed
      return this.formatReportCurrency(trimmed)
    },

    initializeReportInputDefaults() {
      if (!this.keyAuthorisedSignatory) {
        const shareholders = this.resolveShareholdersDisplay(this.reportContext);
        const signatory = shareholders || this.getReportContextValue(
          'key_authorised_signatory',
          'key_shareholder',
          'keyShareholder'
        );
        if (signatory) {
          this.keyAuthorisedSignatory = String(signatory);
        }
      }

      if (!this.salesAsPerVat) {
        const sales = this.getReportContextValue(
          'revenue_vat',
        );
        if (sales !== undefined && sales !== null && sales !== '') {
          this.salesAsPerVat = String(sales);
        }
      }
    },

    resolveShareholdersDisplay(context = {}) {
      const shareholders = context.shareholders
        ?? context.Shareholders
        ?? null;

      if (shareholders === undefined || shareholders === null || shareholders === '') {
        return '';
      }

      if (typeof shareholders === 'string') {
        return shareholders.trim();
      }

      if (!Array.isArray(shareholders)) {
        return String(shareholders).trim();
      }

      const names = shareholders
        .map((entry) => {
          if (entry === undefined || entry === null || entry === '') return '';
          if (typeof entry === 'string' || typeof entry === 'number') {
            return String(entry).trim();
          }
          if (typeof entry === 'object') {
            return String(
              entry.name
                ?? entry.Name
                ?? entry.shareholder
                ?? entry.Shareholder
                ?? entry.full_name
                ?? entry.fullName
                ?? ''
            ).trim();
          }
          return '';
        })
        .filter(Boolean);

      return names.join(', ');
    },

    formatCreditRecommendation(value) {
      return this.normalizeCreditRecommendation(value).toUpperCase()
    },
    
    onRequirementChange() {
      // Update reportAnalysis with company requirement
      if (this.reportAnalysis) {
        this.$set(this.reportAnalysis, 'company_requirement', this.companyRequirement)
      }
    },
    
    onDescriptionChange() {
      if (this.reportAnalysis) {
        this.$set(this.reportAnalysis, 'company_overview', this.companyDescription)
      }
    },

    onKeyAuthorisedSignatoryChange() {
      if (this.reportAnalysis) {
        this.$set(this.reportAnalysis, 'key_authorised_signatory', this.keyAuthorisedSignatory)
      }
    },

    onSalesAsPerVatChange() {
      if (this.reportAnalysis) {
        this.$set(this.reportAnalysis, 'sales_as_per_vat', this.salesAsPerVat)
      }
    },

    goToPrevPreviewPage(event) {
      if (event && this.isEditableElement(event.target)) return
      if (event) event.preventDefault()

      if (this.currentPreviewPage <= 1) return
      this.currentPreviewPage -= 1
    },

    goToNextPreviewPage(event) {
      if (event && this.isEditableElement(event.target)) return
      if (event) event.preventDefault()

      if (this.currentPreviewPage >= this.totalPreviewPages) return
      this.currentPreviewPage += 1
    },

    isEditableElement(element) {
      if (!element || !element.tagName) return false

      const tag = element.tagName.toLowerCase()

      return tag === 'input'
        || tag === 'textarea'
        || tag === 'select'
        || element.isContentEditable
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';

.report-details-page {
  background: #ffffff;
  min-height: 100vh;
}


.main-content {
  padding: 2rem;
  max-width: 1600px;
  margin: 0 auto;
}

.loading-state, .error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  
  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f4f6;
    border-top: 4px solid $color-primary;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }
  
  p {
    color: #6c757d;
    font-size: 1.1rem;
    margin: 0;
  }
}

.error-state {
  .error-icon {
    font-size: 3rem;
    color: #e53e3e;
    margin-bottom: 1rem;
  }
  
  h3 {
    color: #2d3748;
    margin: 0 0 1rem 0;
    font-size: 1.5rem;
  }
  
  .error-actions {
    display: flex;
    gap: 1rem;
    margin-top: 2rem;
  }
  
  .retry-btn, .back-btn {
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: $brand-font-medium;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .retry-btn {
    background: $color-primary;
    color: white;
    
    &:hover {
      background: darken($color-primary, 10%);
    }
  }
  
  .back-btn {
    background: #f8f9fa;
    color: #6c757d;
    border: 1px solid #e2e8f0;
    
    &:hover {
      background: #e9ecef;
    }
  }
}

.report-details-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.report-back-action {
  display: flex;
  justify-content: flex-start;
}

.report-overview-card {
  background: #ffffff;
  
  .report-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;

    i {
      font-size: 3rem;
      color: $color-primary;
    }
    
    .report-info {
      flex: 1;
      
      .report-title {
        font-size: 1.5rem;
        font-weight: 600;
        color: #2d3748;
        margin: 0 0 0.5rem 0;
        font-family: $brand-font-medium;
      }
      
      .report-meta {
        display: flex;
        gap: 1rem;
        flex-wrap: wrap;
        
        span {
          color: #6c757d;
          font-size: 0.9rem;
          font-family: $brand-font-regular;
        }
      }
    }
    
    .report-actions-header {
      display: flex;
      align-items: center;
      gap: 1rem;
    }
  }
}


.report-actions-header {
  .action-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: $brand-font-medium;
    
    &.primary {
      background: $color-primary;
      color: $color-secondary;
      
      &:hover:not(:disabled) {
        background: darken($color-primary, 10%);
      }

      i {
        color: #ef4444;
        font-size: 1.6rem;
      }
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    }
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Credit Model Report Styles */
.credit-model-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.report-editor-layout {
  display: grid;
  grid-template-columns: minmax(320px, 1fr) 816px;
  gap: 2rem;
  align-items: start;
}

.report-inputs-panel {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
}

.panel-page-label {
  margin: -0.25rem 0 0.75rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
}

.report-page-inputs {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.revenue-notes-card {
  margin-top: 1rem;
}

.report-inputs-empty {
  margin: 0.5rem 0 0;
  padding: 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #64748b;
  text-align: center;
  background: #f8fafc;
}

.report-preview-column {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
}

.report-page-navigator {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.65rem 0.85rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  position: sticky;
  top: 1.5rem;
  z-index: 2;
}

.report-page-nav-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;

  i {
    font-size: 1.25rem;
    color: #94a3b8;
    transition: color 0.15s ease;
  }

  &:hover:not(:disabled) i {
    color: #0f172a;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
}

.report-page-nav-meta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
  min-width: 0;
  text-align: center;
}

.report-page-nav-count {
  font-size: 0.82rem;
  font-weight: 600;
  color: #334155;
}

.report-page-nav-title {
  font-size: 0.78rem;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 420px;
}

.field-hint-inline {
  margin-top: -0.15rem;
}

.panel-title {
  margin: 0 0 0.75rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
}

.field-hint {
  margin: -0.25rem 0 0.5rem;
  font-size: 0.8rem;
  color: #6b7280;
}

.report-preview-panel {
  min-width: 0;
}

.report-preview-inputs-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-top: 0.5rem;
}

.payer-stats-form-section {
  margin-top: 0;
}

.payer-stats-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border-top: 1px solid #111827;
  border-left: 1px solid #111827;
  table-layout: fixed;
  font-size: 12px;

  th,
  td {
    border: none;
    border-right: 1px solid #111827;
    border-bottom: 1px solid #111827;
    padding: 6px 8px;
    vertical-align: middle;
    text-align: left;
  }

  thead th {
    background: #4b5563;
    color: #ffffff;
    font-weight: 700;
  }

  tbody td.label-cell {
    font-weight: 700;
    width: 46%;
  }

  tbody td:nth-child(2),
  tbody td:nth-child(3) {
    width: 27%;
  }
}

.payer-stat-cell-input {
  width: 100%;
  padding: 4px 6px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  font-family: $brand-font-regular;
  color: #2d3748;
  background: #ffffff;
  transition: border-color 0.15s ease;

  &:focus {
    outline: none;
    border-color: $color-primary;
    box-shadow: 0 0 0 2px rgba($color-primary, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
}

/* Form Section - Used for company requirement, company description, etc. */
.form-section {
  margin-top: 1rem;

  .section-title {
    display: block;
    font-size: 1rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0 0 0.625rem;
    font-family: $brand-font-medium;

    &::before {
      display: none;
      content: none;
    }
  }

  &:first-child {
    margin-top: 0;
  }
}

/* Base Input Styles - Shared by textarea and input fields */
%base-input {
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: $brand-font-regular;
  color: #2d3748;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: $color-primary;
    box-shadow: 0 0 0 3px rgba($color-primary, 0.1);
  }
  
  &::placeholder {
    color: #adb5bd;
  }
}

/* Textarea Input - Uses base input styles */
.textarea-input {
  @extend %base-input;
  line-height: 1.6;
  resize: vertical;
  min-height: 100px;
}

.text-input {
  @extend %base-input;
}

.recommendation-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-start;
}

.recommendation-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: #495057;
  font-family: $brand-font-medium;
  letter-spacing: 0.3px;
}

.recommendation-badge-compact {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.625rem 1.5rem;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 700;
  font-family: $brand-font-medium;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &.recommendation-accept {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
  }
  
  &.recommendation-reject {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
  }
  
  &.recommendation-review {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    color: white;
  }
}


.recommendation-display {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  flex-wrap: wrap;
}

/* Base Icon Small - Shared styles for small action icons */
%icon-small {
  cursor: pointer;
  font-size: 1.2rem;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.edit-icon-small {
  @extend %icon-small;
  color: #6c757d;
  background: #f8f9fa;
  
  &:hover {
    background: #e9ecef;
    color: $color-primary;
  }
}

.recommendation-edit {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  flex-wrap: wrap;
}

.recommendation-select {
  padding: 0.625rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 700;
  font-family: $brand-font-medium;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  background: white;
  color: #2d3748;
  min-width: 150px;
  
  &:focus {
    outline: none;
    border-color: $color-primary;
    box-shadow: 0 0 0 3px rgba($color-primary, 0.15);
  }
  
  &:hover {
    border-color: #cbd5e0;
  }
  
  option {
    text-transform: uppercase;
    padding: 0.5rem;
  }
}

.save-icon-small {
  @extend %icon-small;
  color: #10b981;
  background: #d1fae5;
  
  &:hover {
    background: #10b981;
    color: white;
  }
}

.cancel-icon-small {
  @extend %icon-small;
  color: #dc2626;
  background: #fee2e2;
  
  &:hover {
    background: #dc2626;
    color: white;
  }
}

@media (max-width: 1320px) {
  .report-editor-layout {
    grid-template-columns: 1fr;
  }

  .report-preview-panel {
    position: static;
    max-height: none;
    overflow: visible;
  }
}

@media (max-width: 768px) {
  .main-content {
    padding: 1rem;
  }
  
  .report-header {
    flex-direction: column;
    text-align: center;
    
    .report-actions-header {
      width: 100%;
      justify-content: center;
      margin-top: 1rem;
    }
  }
}
</style>

